/**
 * ProductieView — Daily production stats (packed items per day)
 */

import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { ProductieDag } from '../../stores/wasserij-store';

interface ProductieViewProps {
  productie: ProductieDag[];
  avg14d: { items: number; m2: number };
  isLoading: boolean;
}

export function ProductieView({ productie, avg14d, isLoading }: ProductieViewProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (productie.length === 0) {
    return (
      <p className="text-muted text-center py-6">Geen productiedata beschikbaar</p>
    );
  }

  // Find max for bar chart scaling
  const maxM2 = Math.max(...productie.map(d => d.totalM2 || 0), 1);

  return (
    <div>
      {/* Average summary */}
      <div className="flex gap-6 mb-4 text-sm">
        <div>
          <span className="text-slate-500">Gem. per dag: </span>
          <span className="font-semibold text-slate-700">{avg14d.items} items</span>
        </div>
        <div>
          <span className="text-slate-500">Gem. m²/dag: </span>
          <span className="font-semibold text-slate-700">{avg14d.m2} m²</span>
        </div>
      </div>

      {/* Table with inline bar chart */}
      <div className="space-y-1.5">
        {productie.slice(0, 14).map(dag => {
          const barWidth = maxM2 > 0 ? Math.max((dag.totalM2 / maxM2) * 100, 2) : 0;
          const m2PerItem = dag.itemCount > 0 ? (dag.totalM2 / dag.itemCount).toFixed(1) : '-';

          return (
            <div key={dag.datum} className="flex items-center gap-3">
              <span className="text-xs text-slate-500 w-[70px] flex-shrink-0">
                {format(parseISO(dag.datum), 'EEE d MMM', { locale: nl })}
              </span>
              <div className="flex-1 flex items-center gap-2">
                <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                  <div
                    className="bg-indigo-400 h-full rounded-full transition-all"
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-slate-700 w-[50px] text-right">
                  {Math.round(dag.totalM2)} m²
                </span>
              </div>
              <span className="text-xs text-slate-400 w-[55px] text-right">
                {dag.itemCount} st
              </span>
              <span className="text-xs text-slate-400 w-[55px] text-right">
                {m2PerItem} m²/st
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
